import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ChevronRight, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * What the coach actually read from the document vault.
 *
 * The model is told to cite the document it answers from, but a citation in
 * prose is unverifiable — it looks the same whether the passage was retrieved
 * or invented. This renders the retrieval result itself: which documents were
 * hit, and the exact excerpt the answer was built on, one click away.
 *
 * It also makes the two kinds of silence visible. `no_documents` and
 * `no_match` produce identical prose ("jag hittade inget") but mean opposite
 * things to the person reading it — one is "upload something", the other is
 * "your documents don't cover this".
 */

/** Longest excerpt shown before clipping — enough to judge relevance. */
const MAX_EXCERPT_CHARS = 400;

export interface VaultSource {
  title: string;
  summary: string | null;
  excerpts: string[];
}

export interface VaultSearchOutput {
  status: "ok" | "no_documents" | "no_match" | "error";
  sources: VaultSource[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function parseExcerpts(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const record = asRecord(entry);
      const text = record?.text;
      return typeof text === "string" ? text.trim() : "";
    })
    .filter((text) => text.length > 0);
}

/**
 * Reads the `intelligence_search_documents` payload defensively — a tool result
 * that predates this shape (or comes back malformed) yields `null` so the chat
 * falls back to showing nothing rather than an empty "sources" box.
 */
export function parseVaultSearchOutput(
  output: unknown,
): VaultSearchOutput | null {
  const record = asRecord(output);
  if (!record) return null;

  const status = record.status;
  if (status === "error" || record.isError === true) {
    return { status: "error", sources: [] };
  }
  if (status !== "ok" && status !== "no_documents" && status !== "no_match") {
    return null;
  }

  const sources: VaultSource[] = Array.isArray(record.documents)
    ? record.documents.flatMap((entry) => {
        const doc = asRecord(entry);
        const title = doc?.document;
        if (typeof title !== "string" || !title.trim()) return [];
        return [
          {
            title: title.trim(),
            summary: typeof doc?.summary === "string" ? doc.summary : null,
            excerpts: parseExcerpts(doc?.excerpts),
          },
        ];
      })
    : [];

  // A payload claiming `ok` with nothing usable in it reads as a match that
  // cannot be shown; treat it as no match rather than an empty source list.
  if (status === "ok" && sources.length === 0) {
    return { status: "no_match", sources: [] };
  }

  return { status, sources };
}

function SourceRow({ source }: { source: VaultSource }) {
  const [open, setOpen] = useState(false);
  const joined = source.excerpts.join("\n\n");
  const excerpt =
    joined.length > MAX_EXCERPT_CHARS
      ? `${joined.slice(0, MAX_EXCERPT_CHARS).trimEnd()}…`
      : joined;
  const expandable = excerpt.length > 0;

  const label = (
    <>
      <FileText className="h-3 w-3 shrink-0 text-muted-foreground/60" />
      <span className="truncate font-medium text-foreground/90">
        {source.title}
      </span>
    </>
  );

  return (
    <li>
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full items-center gap-1.5 text-left transition-colors hover:text-foreground"
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform",
              open && "rotate-90",
            )}
          />
          {label}
        </button>
      ) : (
        <div className="flex items-center gap-1.5 pl-[18px]">{label}</div>
      )}
      {open && (
        <p className="mt-1 whitespace-pre-wrap border-l-2 border-border pl-2 text-[11px] leading-snug text-muted-foreground">
          {excerpt}
        </p>
      )}
    </li>
  );
}

export function VaultSourcesWidget({ output }: { output: unknown }) {
  const { t } = useTranslation("intelligence");
  const parsed = parseVaultSearchOutput(output);
  if (!parsed) return null;

  if (parsed.status === "error") {
    return (
      <div className="mt-1 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {t("tool.vault.failed")}
      </div>
    );
  }

  if (parsed.status !== "ok") {
    return (
      <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
        {parsed.status === "no_documents"
          ? t("tool.vault.noDocuments")
          : t("tool.vault.noMatch")}
      </div>
    );
  }

  return (
    <div className="mt-1 max-w-sm rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
      <div className="mb-1 font-medium text-foreground/90">
        {t("tool.vault.title", { count: parsed.sources.length })}
      </div>
      <ul className="space-y-1">
        {parsed.sources.map((source, index) => (
          <SourceRow key={`${source.title}-${index}`} source={source} />
        ))}
      </ul>
    </div>
  );
}
